import React, { useEffect, useRef } from 'react';

const HeroNetworkBackground = () => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let animFrameId = null;
    let isTabVisible = !document.hidden;
    let isInViewport = true;

    let width = 0;
    let height = 0;

    const resizeCanvas = () => {
      if (!container || !canvas) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = container.offsetWidth;
      height = container.offsetHeight;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Node Count: ~48 on desktop, ~24 on mobile
    const isMobile = width < 768;
    const nodeCount = isMobile ? 24 : 48;
    const nodes = [];

    for (let i = 0; i < nodeCount; i++) {
      const isGold = Math.random() > 0.2;
      nodes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        radius: Math.random() * 1.2 + 1.6,
        color: isGold ? 'rgba(245, 166, 35, 0.55)' : 'rgba(255, 255, 255, 0.18)',
      });
    }

    const pulses = [];
    let lastPulseTime = performance.now();

    const renderFrame = (timestamp) => {
      ctx.clearRect(0, 0, width, height);

      const activeConnections = [];
      const maxDistance = isMobile ? 120 : 155;

      // Draw Proximity Connection Lines
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < maxDistance) {
            activeConnections.push({ from: nodes[i], to: nodes[j] });
            const alpha = (1 - dist / maxDistance) * 0.14;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(245, 166, 35, ${alpha})`;
            ctx.lineWidth = 0.85;
            ctx.stroke();
          }
        }
      }

      // Periodically spawn a signal pulse along a random active connection
      if (!prefersReducedMotion && timestamp - lastPulseTime > 2400 && activeConnections.length > 0) {
        lastPulseTime = timestamp;
        const conn = activeConnections[Math.floor(Math.random() * activeConnections.length)];
        pulses.push({
          fromX: conn.from.x,
          fromY: conn.from.y,
          toX: conn.to.x,
          toY: conn.to.y,
          progress: 0,
          speed: 0.012 + Math.random() * 0.008,
        });
      }

      // Draw and update active signal pulses
      for (let p = pulses.length - 1; p >= 0; p--) {
        const pulse = pulses[p];
        pulse.progress += pulse.speed;

        if (pulse.progress >= 1) {
          pulses.splice(p, 1);
          continue;
        }

        const px = pulse.fromX + (pulse.toX - pulse.fromX) * pulse.progress;
        const py = pulse.fromY + (pulse.toY - pulse.fromY) * pulse.progress;
        const pulseAlpha = Math.sin(pulse.progress * Math.PI);

        ctx.save();
        ctx.beginPath();
        ctx.arc(px, py, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(245, 166, 35, ${pulseAlpha * 0.95})`;
        ctx.shadowColor = 'rgba(245, 166, 35, 0.9)';
        ctx.shadowBlur = 8;
        ctx.fill();
        ctx.restore();
      }

      // Draw and update nodes
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];

        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fillStyle = node.color;
        ctx.fill();

        if (!prefersReducedMotion) {
          node.x += node.vx;
          node.y += node.vy;

          if (node.x < 0 || node.x > width) node.vx *= -1;
          if (node.y < 0 || node.y > height) node.vy *= -1;
        }
      }
    };

    const loop = (timestamp) => {
      if (!isTabVisible || !isInViewport) return;
      renderFrame(timestamp);
      animFrameId = requestAnimationFrame(loop);
    };

    const startAnimation = () => {
      if (!animFrameId && !prefersReducedMotion && isTabVisible && isInViewport) {
        animFrameId = requestAnimationFrame(loop);
      }
    };

    const stopAnimation = () => {
      if (animFrameId) {
        cancelAnimationFrame(animFrameId);
        animFrameId = null;
      }
    };

    if (prefersReducedMotion) {
      renderFrame(performance.now());
    } else {
      startAnimation();
    }

    const handleVisibilityChange = () => {
      isTabVisible = !document.hidden;
      if (isTabVisible) {
        startAnimation();
      } else {
        stopAnimation();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const observer = new IntersectionObserver(
      ([entry]) => {
        isInViewport = entry.isIntersecting;
        if (isInViewport) {
          startAnimation();
        } else {
          stopAnimation();
        }
      },
      { threshold: 0.05 }
    );
    observer.observe(container);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      observer.disconnect();
      stopAnimation();
    };
  }, []);

  return (
    <div ref={containerRef} className="absolute inset-0 pointer-events-none overflow-hidden select-none z-0">
      <canvas ref={canvasRef} className="w-full h-full block" />
    </div>
  );
};

export default HeroNetworkBackground;
